import { Head, router } from "@inertiajs/react"
import {
  Loader2,
  Minus,
  RefreshCw,
  TrendingDown,
  TrendingUp,
} from "lucide-react"
import { useState } from "react"

import { formatDate } from "@/components/products/work_orders/status-chip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem, ScorecardCategory } from "@/types"

interface MetricRow {
  id: number
  metric_key: string
  label: string
  category: ScorecardCategory
  value: number | null
  target: number | null
  unit: string | null
  missing_data: boolean
  delta: number | null
  /** Fewer days is better; higher percent is better. Drives the delta color. */
  lower_is_better: boolean
  on_target: boolean | null
}

interface Props {
  scorecard: {
    id: number
    period: string
    generated_at: string | null
    notes: string | null
    scoreable_type: "Lessor" | "MruTeam"
    scoreable_id: number
  }
  subject: { id: number; type: string; name: string; label: string }
  periods: { id: number; period: string; current: boolean }[]
  categories: ScorecardCategory[]
  metrics: { category: ScorecardCategory; metrics: MetricRow[] }[]
  can_edit: boolean
}

/**
 * One quarter's grid. A PURE RENDER of persisted ScorecardMetric rows —
 * Scorecards::Builder computes and writes them, and this page never recomputes
 * on read. The row is the record of what was SHOWN, and a card that silently
 * changed its numbers between two viewings would be worthless in the meeting
 * it's built for.
 */
export default function ScorecardShow({
  scorecard,
  subject,
  periods,
  metrics,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()
  const [recomputing, setRecomputing] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Scorecards", href: routes.scorecardsPath() },
    { title: subject.name, href: routes.scorecardPath(scorecard.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${subject.name} — ${scorecard.period}`} />

      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">{subject.name}</h1>
              <Badge variant="secondary">{subject.label}</Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-sm">
              {scorecard.generated_at
                ? `Generated ${formatDate(scorecard.generated_at)}`
                : "Not yet generated"}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {periods.length > 1 && (
              <Select
                value={String(scorecard.id)}
                onValueChange={(id) => router.visit(routes.scorecardPath(id))}
              >
                <SelectTrigger className="w-36" aria-label="Period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {periods.map((period) => (
                    <SelectItem key={period.id} value={String(period.id)}>
                      {period.period}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {/* The page never recomputes on read, so this is the only way the
                numbers move — an explicit, attributable act, not a side effect
                of opening the card. */}
            {can_edit && (
              <Button
                size="sm"
                variant="outline"
                disabled={recomputing}
                onClick={() => {
                  setRecomputing(true)
                  router.post(
                    routes.recomputeScorecardPath(scorecard.id),
                    {},
                    {
                      preserveScroll: true,
                      onFinish: () => setRecomputing(false),
                    },
                  )
                }}
              >
                {recomputing ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-1 size-4" />
                )}
                Recompute from data
              </Button>
            )}
          </div>
        </div>

        {scorecard.notes && (
          <p className="text-muted-foreground text-sm">{scorecard.notes}</p>
        )}

        {metrics.length === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground py-12 text-center text-sm">
              This period has no metrics recorded.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {metrics.map((group) => (
              <Card key={group.category}>
                <CardHeader>
                  <CardTitle className="text-base">{group.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y">
                    {group.metrics.map((metric) => (
                      <MetricLine key={metric.id} metric={metric} />
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  )
}

function MetricLine({ metric }: { metric: MetricRow }) {
  return (
    <li className="flex items-center justify-between gap-4 py-2.5">
      <span className="text-sm">{metric.label}</span>

      <div className="flex items-center gap-3">
        {metric.target != null && (
          <span className="text-muted-foreground text-xs tabular-nums">
            target {format(metric.target, metric.unit)}
          </span>
        )}

        <span
          className={cn(
            "text-sm font-semibold tabular-nums",
            // NO DATA IS NOT ZERO. A quarter with nothing to grade says so,
            // and is never colored as if it hit target.
            metric.missing_data && "text-muted-foreground font-normal",
            metric.on_target === true &&
              "text-emerald-600 dark:text-emerald-400",
            metric.on_target === false && "text-destructive",
          )}
        >
          {metric.missing_data ? "No data" : format(metric.value, metric.unit)}
        </span>

        <DeltaBadge metric={metric} />
      </div>
    </li>
  )
}

function DeltaBadge({ metric }: { metric: MetricRow }) {
  if (metric.delta == null || metric.delta === 0) {
    return (
      <span className="text-muted-foreground w-16 text-right text-xs">
        {metric.delta === 0 ? <Minus className="ml-auto size-3.5" /> : "—"}
      </span>
    )
  }

  const improved = metric.lower_is_better ? metric.delta < 0 : metric.delta > 0

  return (
    <span
      className={cn(
        "flex w-16 items-center justify-end gap-0.5 text-xs tabular-nums",
        improved
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-destructive",
      )}
      title="Change from the previous period"
    >
      {metric.delta > 0 ? (
        <TrendingUp className="size-3.5" />
      ) : (
        <TrendingDown className="size-3.5" />
      )}
      {Math.abs(metric.delta)}
    </span>
  )
}

function format(value: number | null, unit: string | null): string {
  if (value == null) return "—"
  if (unit === "percent") return `${value}%`
  if (unit === "days") return `${value}d`
  return String(value)
}
